// ./src/utils/api/VolunteerApplicationService.ts
//
// VolunteerApplication — a person's public sign-up to volunteer on an event (#A4.6). Sign-ups arrive from the
// PUBLIC event website; a host never creates or edits one — they read it and DECIDE its shifts.
//
// ⚠ DECIDING IS NOT A STATUS UPDATE. Approving a shift places the volunteer (guest row + crew assignment) under a
// lock on the shift, checking places and the time rule; withdrawing gives the place back and voids unredeemed
// rewards. One server action per decision, all-or-nothing across the shifts it names — refusals come back keyed
// `shift:<id>` so the drawer shows them on that shift.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";
import { VolunteerSlot } from "./VolunteerSlotService";

// =============================================================================
// Vocabulary — mirrors src/boundary/volunteer.ts
// =============================================================================

export const VOLUNTEER_SHIFT_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  DECLINED: "declined",
  WITHDRAWN: "withdrawn",
} as const;

export const VOLUNTEER_SHIFT_STATUS_LABELS: Record<string, string> = {
  [VOLUNTEER_SHIFT_STATUS.PENDING]: "Waiting on you",
  [VOLUNTEER_SHIFT_STATUS.APPROVED]: "Approved",
  [VOLUNTEER_SHIFT_STATUS.DECLINED]: "Declined",
  [VOLUNTEER_SHIFT_STATUS.WITHDRAWN]: "Withdrawn",
};

export const VOLUNTEER_SHIFT_STATUS_COLORS: Record<string, string> = {
  [VOLUNTEER_SHIFT_STATUS.PENDING]: "orange",
  [VOLUNTEER_SHIFT_STATUS.APPROVED]: "green",
  [VOLUNTEER_SHIFT_STATUS.DECLINED]: "gray",
  [VOLUNTEER_SHIFT_STATUS.WITHDRAWN]: "gray",
};

export const volunteerShiftStatusLabel = (status?: string | null): string => (status && VOLUNTEER_SHIFT_STATUS_LABELS[status]) || "—";

/** The three host decisions — mirrors the API's VOLUNTEER_DECISION. */
export const VOLUNTEER_DECISION = {
  APPROVE: "approve",
  DECLINE: "decline",
  WITHDRAW: "withdraw",
} as const;

export type VolunteerDecision = (typeof VOLUNTEER_DECISION)[keyof typeof VOLUNTEER_DECISION];

// =============================================================================
// GraphQL
// =============================================================================

const SHIFT_FIELDS = `
  id
  volunteerApplicationId
  eventVolunteerSlotId
  applicationStatus
  reviewedAt
  reviewNotes
  withdrawnByApplicant
  eventVolunteerAssignmentId
  createdAt
  slot {
    id name volunteerRoleId startsAt endsAt locationKind locationLabel streetAddress quantityNeeded assignedCount status
  }
`;

export const VOLUNTEER_APPLICATION_FIELDS = `
  id
  accountId
  eventId
  firstName
  lastName
  email
  phone
  message
  submittedAt
  personId
  eventGuestId
  status
  createdAt
  updatedAt
  shifts { ${SHIFT_FIELDS} }
`;

const LIST_QUERY = gql`
  query VolunteerApplicationList($filter: VolunteerApplicationFilter, $params: PaginationAndSorting) {
    volunteerApplicationList(filter: $filter, params: $params) {
      code
      total
      data { ${VOLUNTEER_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query VolunteerApplicationGet($id: ID) {
    volunteerApplicationGet(id: $id) {
      code
      data { ${VOLUNTEER_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const DECIDE_MUTATION = gql`
  mutation VolunteerApplicationDecide($params: VolunteerApplicationDecideInput) {
    volunteerApplicationDecide(params: $params) {
      code
      data { ${VOLUNTEER_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface VolunteerApplicationShift {
  id?: string;
  volunteerApplicationId?: string | null;
  eventVolunteerSlotId?: string | null;
  /** pending | approved | declined | withdrawn. */
  applicationStatus?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
  /** On a withdrawn shift: true = the volunteer withdrew, false = the host did. */
  withdrawnByApplicant?: boolean | null;
  eventVolunteerAssignmentId?: string | null;
  createdAt?: string | null;
  slot?: VolunteerSlot | null;
}

export interface VolunteerApplication {
  id?: string;
  accountId?: string | null;
  eventId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  message?: string | null;
  submittedAt?: string | null;
  personId?: string | null;
  eventGuestId?: string | null;
  status?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  shifts?: VolunteerApplicationShift[] | null;
}

export interface VolunteerApplicationFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  /** Applications that asked for any of these shifts. */
  eventVolunteerSlotId?: string[] | null;
  /** Applications with at least one shift in any of these states. */
  shiftStatus?: string[] | null;
  searchKeyword?: string | null;
}

export const volunteerName = (a?: VolunteerApplication | null): string =>
  [a?.firstName, a?.lastName].filter(Boolean).join(" ").trim() || a?.email || "—";

/** How many of an application's shifts still wait on the host. */
export const pendingShiftCount = (a?: VolunteerApplication | null): number =>
  (a?.shifts ?? []).filter((s) => s.applicationStatus === VOLUNTEER_SHIFT_STATUS.PENDING).length;

// =============================================================================
// Service
// =============================================================================

export class VolunteerApplicationService {
  static async list(filter?: VolunteerApplicationFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  /**
   * Approve / decline / withdraw requested shifts of ONE application — all or nothing. Approval places the
   * volunteer (the reward comes later, at check-in); withdrawing an approved shift gives the place back.
   */
  static async decide(applicationId: string, shiftIds: string[], decision: VolunteerDecision, reviewNotes?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(DECIDE_MUTATION, {
      params: { applicationId, shiftIds, decision, reviewNotes: reviewNotes || null },
    });
  }
}
